import { useState, useEffect, useMemo, useCallback } from 'react';
import { apiFetch } from '../../../shared/lib/apiClient';
import { useAuth } from '../../auth/context/AuthContext';
import SourceViewerModal from './SourceViewerModal';

const TYPE_LABELS = {
  asset: 'Assets',
  liability: 'Liabilities',
  equity: 'Equity',
  revenue: 'Revenue',
  cogs: 'Cost of Goods Sold',
  expense: 'Expenses',
};

const TYPE_ORDER = ['asset', 'liability', 'equity', 'revenue', 'cogs', 'expense'];

const fmt = (n) => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/* ── Icons ── */
const FolderIcon = ({ open, className = '' }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" className={className}>
    {open ? (
      <>
        <path d="M1.5 3.5A1 1 0 012.5 2.5h3.086a1 1 0 01.707.293L7.5 4H13a1 1 0 011 1v1H3.5a1.5 1.5 0 00-1.434 1.06L1 10.5V3.5z" fill="currentColor" opacity="0.5" />
        <path d="M2.066 7.06A1.5 1.5 0 013.5 6H14.5v.5L13 12.5a1 1 0 01-.962.725H2.538a1 1 0 01-.962-.725L.5 8.5z" fill="currentColor" opacity="0.75" />
      </>
    ) : (
      <path d="M1.5 3A1 1 0 012.5 2h3.086a1 1 0 01.707.293L7.5 3.5H13.5a1 1 0 011 1V12a1 1 0 01-1 1h-11a1 1 0 01-1-1V3z" fill="currentColor" opacity="0.6" />
    )}
  </svg>
);

const ChevronIcon = ({ expanded }) => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor" className={`transition-transform duration-150 ${expanded ? 'rotate-90' : ''} text-zinc-400`}>
    <path d="M3 1l5 4-5 4z" />
  </svg>
);

const ChartOfAccountsView = ({ loaded }) => {
  const { activeCompany } = useAuth();
  const companyName = activeCompany?.company?.name || 'My Company';

  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [addingType, setAddingType] = useState(null);
  const [addingParentId, setAddingParentId] = useState(null);
  const [formData, setFormData] = useState({ account_number: '', name: '', type: 'expense', sub_type: '', description: '', parent_id: '' });
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [lineItemSummaries, setLineItemSummaries] = useState({});
  const [ledgerExpandedId, setLedgerExpandedId] = useState(null);
  const [lineItems, setLineItems] = useState({});
  const [lineItemsLoading, setLineItemsLoading] = useState(null);
  const [search, setSearch] = useState('');
  const [pdfModal, setPdfModal] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState(new Set(['root']));

  /* ── API calls ── */
  const handleViewPdf = async (invoiceId, item, accountId) => {
    setPdfModal({ invoiceId, lineItemId: item.id, item, accountId, url: null, loading: true });
    try {
      const res = await apiFetch(`/api/invoices/${invoiceId}/download`);
      if (!res.ok) throw new Error('Failed to get download URL');
      const data = await res.json();
      setPdfModal(prev => prev ? { ...prev, url: data.downloadUrl, loading: false } : null);
    } catch {
      setPdfModal(null);
      setError('Failed to load invoice PDF');
    }
  };

  const handleSourceItemChange = (newItem) => {
    if (!newItem.invoice_id) return;
    handleViewPdf(newItem.invoice_id, newItem, pdfModal?.accountId);
  };

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const res = await apiFetch('/api/chart-of-accounts');
      if (!res.ok) throw new Error('Failed to fetch accounts');
      const data = await res.json();
      setAccounts(data.accounts || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchLineItemSummaries = async () => {
    try {
      const res = await apiFetch('/api/chart-of-accounts/line-item-summaries');
      const data = await res.json();
      if (res.ok) setLineItemSummaries(data.summaries || {});
    } catch (err) {
      console.error('fetchLineItemSummaries error:', err);
    }
  };

  const handleLedgerExpand = async (accountId) => {
    if (ledgerExpandedId === accountId) { setLedgerExpandedId(null); return; }
    setLedgerExpandedId(accountId);
    if (!lineItems[accountId]) {
      setLineItemsLoading(accountId);
      try {
        const res = await apiFetch(`/api/chart-of-accounts/${accountId}/line-items`);
        if (res.ok) {
          const data = await res.json();
          setLineItems(prev => ({ ...prev, [accountId]: data.lineItems || [] }));
        }
      } catch {} finally { setLineItemsLoading(null); }
    }
  };

  useEffect(() => { fetchAccounts(); fetchLineItemSummaries(); }, []);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const res = await apiFetch('/api/chart-of-accounts/seed', { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to seed');
      if (data.seeded) {
        setSuccessMessage(`Created ${data.count} default accounts`);
        setTimeout(() => setSuccessMessage(null), 3000);
        fetchAccounts();
      } else { setError(data.message); }
    } catch (err) { setError(err.message); }
    finally { setSeeding(false); }
  };

  const handleResetAndReseed = async () => {
    if (!window.confirm('This will delete all existing accounts and re-create the default hierarchy. Line item categorizations will be cleared. Continue?')) return;
    setSeeding(true);
    try {
      const res = await apiFetch('/api/chart-of-accounts/reset-and-reseed', { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to reset');
      setSuccessMessage(`Reset complete — created ${data.count} accounts`);
      setTimeout(() => setSuccessMessage(null), 3000);
      setExpandedNodes(new Set(['root']));
      fetchAccounts();
      fetchLineItemSummaries();
    } catch (err) { setError(err.message); }
    finally { setSeeding(false); }
  };

  /* ── CRUD handlers ── */
  const getNextAccountNumber = (type) => {
    const ta = accounts.filter(a => a.type === type);
    if (!ta.length) return ({ asset: '1000', liability: '2000', equity: '3000', revenue: '4000', cogs: '5000', expense: '6000' })[type] || '1000';
    return String(Math.max(...ta.map(a => parseInt(a.account_number) || 0)) + 10);
  };

  const handleAdd = (type) => {
    setEditingId(null);
    setAddingParentId(null);
    setAddingType(type);
    setFormData({ account_number: getNextAccountNumber(type), name: '', type, sub_type: '', description: '', parent_id: '' });
    setExpandedNodes(prev => { const n = new Set(prev); n.add('root'); n.add(`type:${type}`); return n; });
  };

  const handleAddChild = (parentAccount) => {
    setEditingId(null);
    setAddingType(parentAccount.type);
    setAddingParentId(parentAccount.id);
    setFormData({ account_number: '', name: '', type: parentAccount.type, sub_type: '', description: '', parent_id: parentAccount.id });
    setExpandedNodes(prev => {
      const n = new Set(prev);
      n.add('root');
      n.add(`type:${parentAccount.type}`);
      n.add(parentAccount.id);
      return n;
    });
  };

  const handleEdit = (account) => {
    setAddingType(null);
    setAddingParentId(null);
    setEditingId(account.id);
    setFormData({ account_number: account.account_number, name: account.name, type: account.type, sub_type: account.sub_type || '', description: account.description || '', parent_id: account.parent_id || '' });
  };

  const handleCancel = () => { setAddingType(null); setEditingId(null); setAddingParentId(null); };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.account_number.trim()) return;
    setSaving(true);
    try {
      const body = { ...formData, parent_id: formData.parent_id || null, sub_type: formData.sub_type || null, description: formData.description || null };
      const res = editingId
        ? await apiFetch(`/api/chart-of-accounts/${editingId}`, { method: 'PUT', body: JSON.stringify(body) })
        : await apiFetch('/api/chart-of-accounts', { method: 'POST', body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save');
      setSuccessMessage(editingId ? 'Account updated' : 'Account created');
      setTimeout(() => setSuccessMessage(null), 3000);
      handleCancel();
      fetchAccounts();
      fetchLineItemSummaries();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const handleArchive = async (account) => {
    try {
      const res = await apiFetch(`/api/chart-of-accounts/${account.id}/archive`, { method: 'POST' });
      if (!res.ok) throw new Error('Failed to archive');
      setSuccessMessage(`Archived "${account.name}"`);
      setTimeout(() => setSuccessMessage(null), 3000);
      fetchAccounts();
    } catch (err) { setError(err.message); }
  };

  /* ── Tree expand/collapse ── */
  const toggleNode = useCallback((nodeId) => {
    setExpandedNodes(prev => {
      const n = new Set(prev);
      if (n.has(nodeId)) n.delete(nodeId);
      else n.add(nodeId);
      return n;
    });
  }, []);

  const expandAll = useCallback(() => {
    const all = new Set(['root']);
    TYPE_ORDER.forEach(t => all.add(`type:${t}`));
    accounts.forEach(a => all.add(a.id));
    setExpandedNodes(all);
  }, [accounts]);

  const collapseAll = useCallback(() => {
    setExpandedNodes(new Set());
  }, []);

  /* ── Filter & build tree ── */
  const filtered = useMemo(() => {
    return accounts.filter(a => {
      if (!showInactive && !a.is_active) return false;
      return true;
    });
  }, [accounts, showInactive]);

  const matchingIds = useMemo(() => {
    if (!search) return null;
    const q = search.toLowerCase();
    const matches = new Set();
    const aMap = {};
    for (const a of filtered) aMap[a.id] = a;
    for (const a of filtered) {
      if (a.name.toLowerCase().includes(q) || a.account_number.includes(q) || (a.description || '').toLowerCase().includes(q)) {
        matches.add(a.id);
        let pid = a.parent_id;
        while (pid && aMap[pid]) { matches.add(pid); pid = aMap[pid].parent_id; }
      }
    }
    return matches;
  }, [search, filtered]);

  const { typeRoots, accountMap } = useMemo(() => {
    const map = {};
    for (const a of filtered) map[a.id] = { ...a, children: [] };
    const roots = {};
    for (const t of TYPE_ORDER) roots[t] = [];
    for (const a of filtered) {
      if (matchingIds && !matchingIds.has(a.id)) continue;
      if (a.parent_id && map[a.parent_id] && (!matchingIds || matchingIds.has(a.parent_id))) {
        map[a.parent_id].children.push(map[a.id]);
      } else {
        roots[a.type]?.push(map[a.id]);
      }
    }
    const sortNodes = (nodes) => {
      nodes.sort((a, b) => (parseInt(a.account_number) || 0) - (parseInt(b.account_number) || 0));
      nodes.forEach(n => sortNodes(n.children));
    };
    for (const t of TYPE_ORDER) sortNodes(roots[t]);
    return { typeRoots: roots, accountMap: map };
  }, [filtered, matchingIds]);

  useEffect(() => {
    if (matchingIds && matchingIds.size > 0) {
      const autoExpand = new Set(['root']);
      for (const a of filtered) { if (matchingIds.has(a.id)) autoExpand.add(`type:${a.type}`); }
      matchingIds.forEach(id => { if (accountMap[id]?.children?.length > 0) autoExpand.add(id); });
      setExpandedNodes(autoExpand);
    }
  }, [matchingIds]);

  const getNodeTotal = useCallback((nodeId) => {
    const direct = lineItemSummaries[nodeId]?.total || 0;
    const node = accountMap[nodeId];
    if (!node) return direct;
    return direct + (node.children || []).reduce((s, c) => s + getNodeTotal(c.id), 0);
  }, [lineItemSummaries, accountMap]);

  const getTypeTotal = useCallback((type) => {
    return (typeRoots[type] || []).reduce((s, a) => s + getNodeTotal(a.id), 0);
  }, [typeRoots, getNodeTotal]);

  const isEmpty = accounts.length === 0 && !loading;

  const anim = (delay) => ({
    opacity: loaded ? 1 : 0,
    transform: loaded ? 'translateY(0)' : 'translateY(12px)',
    transitionDelay: `${delay}ms`,
    transitionProperty: 'opacity, transform',
    transitionDuration: '700ms',
    transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
  });

  /* ── Tree connector lines ── */
  const LEVEL_W = 32; // px per indent level
  const LINE_X = 12;  // x-offset for vertical line within each level
  const BRANCH_W = 20; // horizontal branch length

  // Renders vertical connector lines for ancestor levels (used by ledger panel etc.)
  const TreeConnectors = ({ parentLines }) => {
    const depth = parentLines.length;
    return (
      <div className="flex items-stretch shrink-0 self-stretch" style={{ width: `${depth * LEVEL_W}px` }}>
        {parentLines.map((hasLine, i) => (
          <div key={i} className="relative" style={{ width: `${LEVEL_W}px` }}>
            {hasLine && (
              <div className="absolute top-0 bottom-0 w-px bg-zinc-200" style={{ left: `${LINE_X}px` }} />
            )}
          </div>
        ))}
      </div>
    );
  };

  // Connector for the current node: vertical line from parent + horizontal branch
  const NodeConnector = ({ parentLines, isLast }) => {
    const depth = parentLines.length;
    if (depth === 0) return null;
    return (
      <div className="flex items-stretch shrink-0 self-stretch" style={{ width: `${depth * LEVEL_W}px` }}>
        {parentLines.slice(0, -1).map((hasLine, i) => (
          <div key={i} className="relative" style={{ width: `${LEVEL_W}px` }}>
            {hasLine && (
              <div className="absolute top-0 bottom-0 w-px bg-zinc-200" style={{ left: `${LINE_X}px` }} />
            )}
          </div>
        ))}
        {/* Current connector: vertical half + horizontal branch */}
        <div className="relative" style={{ width: `${LEVEL_W}px` }}>
          <div className={`absolute top-0 w-px bg-zinc-200 ${isLast ? 'h-1/2' : 'h-full'}`} style={{ left: `${LINE_X}px` }} />
          <div className="absolute top-1/2 h-px bg-zinc-200" style={{ left: `${LINE_X}px`, width: `${BRANCH_W}px` }} />
        </div>
      </div>
    );
  };

  /* ── Recursive tree row renderer ── */
  const renderAccountNode = (node, parentLines, isLast) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedNodes.has(node.id);
    const total = getNodeTotal(node.id);
    const lineCount = lineItemSummaries[node.id]?.count || 0;
    const isEditing = editingId === node.id;
    const isLedgerOpen = ledgerExpandedId === node.id;
    const isAddingChild = addingParentId === node.id;

    if (isEditing) {
      return (
        <div key={node.id}>
          <div className="flex items-center" style={{ paddingRight: '16px' }}>
            <NodeConnector parentLines={parentLines} isLast={isLast} />
            <div className="flex-1 min-w-0">
              <EditRow formData={formData} setFormData={setFormData} onSave={handleSave} onCancel={handleCancel} saving={saving} />
            </div>
          </div>
        </div>
      );
    }

    const childLines = [...parentLines, !isLast];

    return (
      <div key={node.id}>
        {/* Row */}
        <div
          className={`flex items-center h-8 group cursor-pointer hover:bg-blue-50/40 transition-colors duration-100 ${
            !node.is_active ? 'opacity-40' : ''
          }`}
          style={{ paddingRight: '16px' }}
          onClick={() => toggleNode(node.id)}
        >
          <NodeConnector parentLines={parentLines} isLast={isLast} />

          {/* Chevron */}
          <div className="w-5 shrink-0 flex items-center justify-center">
            <ChevronIcon expanded={isExpanded} />
          </div>

          {/* Folder icon */}
          <div className="shrink-0 mr-1.5 flex items-center justify-center">
            <FolderIcon open={isExpanded && hasChildren} className="text-blue-500" />
          </div>

          {/* Name + hover actions */}
          <div className="flex-1 min-w-0 flex items-center">
            <span className="text-[13px] truncate text-zinc-800">
              {node.name}
            </span>

            <div className="hidden group-hover:flex items-center gap-px ml-3 shrink-0">
              <button onClick={(e) => { e.stopPropagation(); handleEdit(node); }}
                className="px-1.5 py-0.5 text-[11px] text-zinc-400 hover:text-zinc-800 transition-colors">Edit</button>
              {node.is_active && (
                <button onClick={(e) => { e.stopPropagation(); handleArchive(node); }}
                  className="px-1.5 py-0.5 text-[11px] text-zinc-400 hover:text-red-500 transition-colors">Delete</button>
              )}
              <button onClick={(e) => { e.stopPropagation(); handleAddChild(node); }}
                className="px-1.5 py-0.5 text-[11px] text-zinc-400 hover:text-zinc-800 transition-colors">Add Child</button>
              {lineCount > 0 && (
                <button onClick={(e) => { e.stopPropagation(); handleLedgerExpand(node.id); }}
                  className="px-1.5 py-0.5 text-[11px] text-zinc-400 hover:text-zinc-800 transition-colors">View Ledger</button>
              )}
            </div>
          </div>

          {/* Amount */}
          <div className="shrink-0 text-right" style={{ minWidth: '120px' }}>
            {total > 0 ? (
              <span className="text-[13px] font-mono text-zinc-700">${fmt(total)}</span>
            ) : (
              <span className="text-[13px] font-mono text-zinc-300">$0.00</span>
            )}
          </div>
        </div>

        {/* Ledger panel */}
        {isLedgerOpen && (
          <div className="flex">
            <TreeConnectors parentLines={childLines} />
            <div className="flex-1 min-w-0 pr-4 pb-1">
              <LineItemsPanel
                items={lineItems[node.id]}
                loading={lineItemsLoading === node.id}
                onViewPdf={handleViewPdf}
                activeLineItemId={pdfModal?.lineItemId}
                accountId={node.id}
              />
            </div>
          </div>
        )}

        {/* Add child form */}
        {isAddingChild && (
          <div className="flex items-center" style={{ paddingRight: '16px' }}>
            <div style={{ width: `${childLines.length * LEVEL_W}px` }} className="shrink-0" />
            <div className="w-5 shrink-0" />
            <div className="flex-1 min-w-0">
              <EditRow formData={formData} setFormData={setFormData} onSave={handleSave} onCancel={handleCancel} saving={saving} isNew />
            </div>
          </div>
        )}

        {/* Expanded children */}
        {isExpanded && hasChildren && node.children.map((child, i) =>
          renderAccountNode(child, childLines, i === node.children.length - 1)
        )}
      </div>
    );
  };

  // Count visible type groups (for determining isLast on type nodes)
  const visibleTypes = TYPE_ORDER.filter(type => {
    const hasAccounts = (typeRoots[type] || []).length > 0;
    const isAddingToType = addingType === type && !addingParentId;
    return hasAccounts || isAddingToType;
  });

  return (
    <div className="max-w-[1100px] mx-auto">
      {/* Header */}
      <div style={anim(300)} className="flex items-end justify-between mb-6">
        <div>
          <h1 className="text-2xl font-light text-zinc-900 mb-1">Chart of Accounts</h1>
          <p className="text-sm text-zinc-400">Your financial structure at a glance.</p>
        </div>
      </div>

      {/* Toolbar */}
      {!isEmpty && !loading && (
        <div style={anim(400)} className="flex items-center gap-2 mb-3 flex-wrap">
          <button onClick={expandAll} className="px-3 py-1.5 text-[12px] text-zinc-600 hover:text-zinc-900 transition-colors">
            Expand All
          </button>
          <div className="w-px h-4 bg-zinc-200" />
          <button onClick={collapseAll} className="px-3 py-1.5 text-[12px] text-zinc-600 hover:text-zinc-900 transition-colors">
            Collapse All
          </button>
          <div className="w-px h-4 bg-zinc-200" />
          <label className="flex items-center gap-1.5 text-[12px] text-zinc-500 cursor-pointer select-none px-2">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} className="rounded border-zinc-300 w-3.5 h-3.5" />
            Show archived
          </label>
          <div className="w-px h-4 bg-zinc-200" />
          <button onClick={handleResetAndReseed} disabled={seeding} className="px-3 py-1.5 text-[12px] text-red-400 hover:text-red-600 transition-colors disabled:opacity-50">
            {seeding ? 'Resetting...' : 'Reset & Re-seed'}
          </button>
          <div className="ml-auto relative">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-300" strokeLinecap="round"><circle cx="6" cy="6" r="4.5" /><path d="M9.5 9.5L13 13" /></svg>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className="w-48 pl-8 pr-3 py-1.5 border border-zinc-200 rounded text-[13px] outline-none focus:border-zinc-400 transition-colors bg-white placeholder:text-zinc-300"
            />
          </div>
        </div>
      )}

      {/* Messages */}
      {successMessage && <div className="mb-3 px-4 py-2.5 bg-emerald-50 border border-emerald-200 rounded text-sm text-emerald-700">{successMessage}</div>}
      {error && (
        <div className="mb-3 px-4 py-2.5 bg-red-50 border border-red-200 rounded text-sm text-red-600 flex items-center justify-between">
          {error}
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600 ml-4"><svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 3l8 8M11 3l-8 8" /></svg></button>
        </div>
      )}

      {loading && <div className="flex items-center justify-center py-20"><div className="w-6 h-6 border-2 border-zinc-200 border-t-zinc-600 rounded-full animate-spin" /></div>}

      {/* Empty state */}
      {isEmpty && (
        <div style={anim(400)} className="text-center py-20">
          <div className="w-16 h-16 rounded-2xl bg-zinc-100 flex items-center justify-center mx-auto mb-6">
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-zinc-400" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h20v20H4z" /><path d="M4 10h20M4 16h20M10 4v20" /></svg>
          </div>
          <h2 className="text-lg font-light text-zinc-900 mb-2">No accounts yet</h2>
          <p className="text-sm text-zinc-400 mb-6">Start with our default chart of accounts — you can customize everything later.</p>
          <button onClick={handleSeed} disabled={seeding} className="px-6 py-2.5 bg-zinc-900 text-white text-sm tracking-wider uppercase hover:bg-zinc-800 transition-colors rounded-md disabled:opacity-50 flex items-center gap-2 mx-auto">
            {seeding && <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" />}
            Load Default Accounts
          </button>
        </div>
      )}

      {/* Tree */}
      {!loading && !isEmpty && (
        <div style={anim(500)} className="bg-white border border-zinc-200 rounded-lg py-2 pl-3 select-none">

          {/* Root node */}
          <div
            className="flex items-center h-9 cursor-pointer hover:bg-zinc-50 transition-colors rounded"
            style={{ paddingRight: '16px' }}
            onClick={() => toggleNode('root')}
          >
            <div className="w-5 shrink-0 flex items-center justify-center">
              <ChevronIcon expanded={expandedNodes.has('root')} />
            </div>
            <div className="shrink-0 mr-1.5 flex items-center justify-center">
              <FolderIcon open={expandedNodes.has('root')} className="text-blue-600" />
            </div>
            <span className="text-[14px] font-semibold text-zinc-900">{companyName}</span>
          </div>

          {/* Type groups */}
          {expandedNodes.has('root') && visibleTypes.map((type, typeIdx) => {
            const typeNodeId = `type:${type}`;
            const typeExpanded = expandedNodes.has(typeNodeId);
            const typeAccounts = typeRoots[type] || [];
            const typeTotal = getTypeTotal(type);
            const hasAccounts = typeAccounts.length > 0;
            const isAddingToType = addingType === type && !addingParentId;
            const isLastType = typeIdx === visibleTypes.length - 1;

            // The type node itself is at depth 1 (child of root)
            // Its children (accounts) need depth 2: [root-continues, type-continues]
            const typeChildLines = [!isLastType, true];

            return (
              <div key={type}>
                {/* Type row */}
                <div
                  className="flex items-center h-8 group cursor-pointer hover:bg-blue-50/30 transition-colors duration-100"
                  style={{ paddingRight: '16px' }}
                  onClick={() => toggleNode(typeNodeId)}
                >
                  <NodeConnector parentLines={[!isLastType]} isLast={isLastType} />

                  <div className="w-5 shrink-0 flex items-center justify-center">
                    {hasAccounts && <ChevronIcon expanded={typeExpanded} />}
                  </div>

                  <div className="shrink-0 mr-1.5 flex items-center justify-center">
                    <FolderIcon open={typeExpanded} className="text-blue-500" />
                  </div>

                  <div className="flex-1 min-w-0 flex items-center">
                    <span className="text-[13px] font-medium text-zinc-700">{TYPE_LABELS[type]}</span>
                    <div className="hidden group-hover:flex items-center ml-3">
                      <button onClick={(e) => { e.stopPropagation(); handleAdd(type); }}
                        className="px-1.5 py-0.5 text-[11px] text-zinc-400 hover:text-zinc-800 transition-colors">+ Add</button>
                    </div>
                  </div>

                  <div className="shrink-0 text-right" style={{ minWidth: '120px' }}>
                    {typeTotal > 0 ? (
                      <span className="text-[13px] font-mono text-zinc-700">${fmt(typeTotal)}</span>
                    ) : (
                      <span className="text-[13px] font-mono text-zinc-300">$0.00</span>
                    )}
                  </div>
                </div>

                {/* Accounts */}
                {typeExpanded && typeAccounts.map((account, i) =>
                  renderAccountNode(account, typeChildLines, i === typeAccounts.length - 1 && !isAddingToType)
                )}

                {/* Add form at type level */}
                {isAddingToType && (
                  <div className="flex items-center" style={{ paddingRight: '16px' }}>
                    <div style={{ width: `${typeChildLines.length * LEVEL_W}px` }} className="shrink-0" />
                    <div className="w-5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <EditRow formData={formData} setFormData={setFormData} onSave={handleSave} onCancel={handleCancel} saving={saving} isNew />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Source Viewer Modal */}
      {pdfModal && !pdfModal.loading && pdfModal.url && (
        <SourceViewerModal
          pdfUrl={pdfModal.url}
          item={pdfModal.item}
          allItems={lineItems[pdfModal.accountId] || []}
          onClose={() => setPdfModal(null)}
          onItemChange={handleSourceItemChange}
        />
      )}
      {pdfModal && pdfModal.loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg px-8 py-6 shadow-xl flex items-center gap-3">
            <div className="w-5 h-5 border-2 border-zinc-200 border-t-zinc-600 rounded-full animate-spin" />
            <span className="text-sm text-zinc-600">Loading invoice...</span>
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Inline edit row ── */
const EditRow = ({ formData, setFormData, onSave, onCancel, saving, isNew }) => (
  <div className="flex items-center gap-2 py-1.5 my-0.5 px-2.5 bg-blue-50/50 border border-blue-200/60 rounded">
    <input
      value={formData.account_number}
      onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
      className="w-14 px-1.5 py-1 border border-zinc-200 rounded text-xs font-mono outline-none focus:border-blue-400 bg-white"
      placeholder="#"
      autoFocus={isNew}
    />
    <input
      value={formData.name}
      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
      className="flex-1 px-2 py-1 border border-zinc-200 rounded text-[13px] outline-none focus:border-blue-400 bg-white"
      placeholder="Account name"
      autoFocus={!isNew}
      onKeyDown={(e) => e.key === 'Enter' && onSave()}
    />
    <input
      value={formData.sub_type}
      onChange={(e) => setFormData({ ...formData, sub_type: e.target.value })}
      className="w-20 px-1.5 py-1 border border-zinc-200 rounded text-xs outline-none focus:border-blue-400 hidden sm:block bg-white"
      placeholder="Sub type"
    />
    <button onClick={onSave} disabled={saving || !formData.name.trim() || !formData.account_number.trim()} className="px-3 py-1 bg-zinc-800 text-white text-[11px] rounded hover:bg-zinc-700 disabled:opacity-40 transition-colors">
      {saving ? '...' : 'Save'}
    </button>
    <button onClick={onCancel} className="px-2 py-1 text-[11px] text-zinc-400 hover:text-zinc-600 transition-colors">Cancel</button>
  </div>
);

/* ── Expanded line items — spreadsheet style ── */
const LineItemsPanel = ({ items, loading, onViewPdf, activeLineItemId, accountId }) => (
  <div className="my-1 rounded overflow-hidden border border-zinc-200">
    <style>{`@keyframes starFlash { 0%,100% { opacity: 1; } 50% { opacity: 0.2; } }`}</style>
    {loading ? (
      <div className="flex items-center gap-2 px-4 py-4 text-xs text-zinc-400">
        <div className="w-3 h-3 border border-zinc-300 border-t-zinc-500 rounded-full animate-spin" />
        Loading...
      </div>
    ) : !items || items.length === 0 ? (
      <div className="px-4 py-4 text-xs text-zinc-400">No categorized invoices</div>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="bg-zinc-700 text-white text-[10px] uppercase tracking-wider">
              <th className="w-9 px-2 py-2 text-center font-medium border-r border-zinc-600">#</th>
              <th className="px-3 py-2 text-left font-medium border-r border-zinc-600 min-w-[200px]">Description</th>
              <th className="px-3 py-2 text-left font-medium border-r border-zinc-600 w-[140px]">Vendor</th>
              <th className="px-3 py-2 text-left font-medium border-r border-zinc-600 w-[100px]">Invoice #</th>
              <th className="px-3 py-2 text-left font-medium border-r border-zinc-600 w-[60px]">Qty</th>
              <th className="px-3 py-2 text-right font-medium border-r border-zinc-600 w-[90px]">Unit Price</th>
              <th className="px-3 py-2 text-right font-medium border-r border-zinc-600 w-[100px]">Amount</th>
              <th className="px-3 py-2 text-left font-medium border-r border-zinc-600 w-[80px]">Status</th>
              <th className="px-3 py-2 text-right font-medium border-r border-zinc-600 w-[90px]">Date</th>
              <th className="w-10 px-2 py-2 text-center font-medium">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="inline-block" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => {
              const isActive = activeLineItemId === item.id;
              return (
                <tr key={item.id} className={`${isActive ? 'bg-amber-50' : i % 2 === 0 ? 'bg-white' : 'bg-zinc-50/70'} hover:bg-blue-50/50 transition-colors`}>
                  <td className="px-2 py-1.5 text-center font-mono border-r border-zinc-100 bg-zinc-50/50">
                    <div className="flex items-center justify-center gap-0.5">
                      {isActive && <span style={{ animation: 'starFlash 1.2s ease-in-out infinite', color: '#f59e0b', fontSize: '12px' }}>&#x2733;</span>}
                      <span className="text-zinc-400">{i + 1}</span>
                    </div>
                  </td>
                  <td className="px-3 py-1.5 text-zinc-800 truncate max-w-[300px] border-r border-zinc-100">{item.description || '—'}</td>
                  <td className="px-3 py-1.5 text-zinc-600 truncate border-r border-zinc-100">{item.invoices?.vendor_name || '—'}</td>
                  <td className="px-3 py-1.5 text-zinc-500 font-mono border-r border-zinc-100">{item.invoices?.invoice_number || '—'}</td>
                  <td className="px-3 py-1.5 text-zinc-600 font-mono border-r border-zinc-100">{item.quantity_shipped ?? item.quantity_ordered ?? '—'}</td>
                  <td className="px-3 py-1.5 text-right text-zinc-600 font-mono border-r border-zinc-100">
                    {item.unit_price != null ? `$${fmt(Number(item.unit_price))}` : '—'}
                  </td>
                  <td className="px-3 py-1.5 text-right text-zinc-900 font-mono font-medium border-r border-zinc-100">
                    {item.extended_amount != null ? `$${fmt(Number(item.extended_amount))}` : '—'}
                  </td>
                  <td className="px-3 py-1.5 border-r border-zinc-100">
                    {item.invoices?.status ? (
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        item.invoices.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                        item.invoices.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                        item.invoices.status === 'rejected' ? 'bg-red-100 text-red-700' :
                        'bg-zinc-100 text-zinc-600'
                      }`}>{item.invoices.status}</span>
                    ) : '—'}
                  </td>
                  <td className="px-3 py-1.5 text-right text-zinc-500 border-r border-zinc-100">
                    {item.invoices?.invoice_date ? new Date(item.invoices.invoice_date + 'T00:00:00').toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) : '—'}
                  </td>
                  <td className="px-2 py-1.5 text-center">
                    {item.invoice_id && (
                      <button
                        onClick={() => onViewPdf(item.invoice_id, item, accountId)}
                        className="text-zinc-300 hover:text-zinc-700 transition-colors"
                        title="View invoice PDF"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="bg-zinc-100 border-t border-zinc-200 font-medium">
              <td className="px-2 py-2 border-r border-zinc-200" />
              <td className="px-3 py-2 text-zinc-500" colSpan={5}>
                {items.length} item{items.length !== 1 ? 's' : ''}
              </td>
              <td className="px-3 py-2 text-right font-mono text-zinc-900 border-r border-zinc-200">
                ${fmt(items.reduce((s, i) => s + (Number(i.extended_amount) || 0), 0))}
              </td>
              <td colSpan={3} />
            </tr>
          </tfoot>
        </table>
      </div>
    )}
  </div>
);

export default ChartOfAccountsView;
